import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, Hindi translation of en.tsx. Every number comes from `limits`; every claim
 * about who can read what follows the threat model (PLAN.md 4.12). Keep it in step with en.tsx.
 */
export function GuideHi({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>Vrelis कैसे काम करता है</h1>
      <p className="lead">
        Vrelis एन्क्रिप्टेड संदेश, यानी टाइम कैप्सूल, भेजने की सेवा है, जिन्हें केवल आपके चुने पल से पढ़ा जा सकता है: पायलट के सभी लोग, या
        एक व्यक्ति। उस पल से पहले कोई कैप्सूल नहीं पढ़ सकता: न वह व्यक्ति जिसे आपने लिखा, न Vrelis चलाने वाले लोग, न आप।
      </p>
      <p className="warn">
        यह एक बंद पायलट है। जो कैप्सूल आप रखना चाहते हैं उन्हें डाउनलोड करें (नीचे देखें), और अपना पुनर्प्राप्ति वाक्यांश सँभालकर रखें।
      </p>

      <h2>लॉक पर भरोसा क्यों किया जा सकता है</h2>
      <p>
        लॉक Vrelis का कोई वादा नहीं है। यह प्रकाशित क्रिप्टोग्राफ़ी और एक सार्वजनिक समय संकेत है, जिस पर Vrelis का नियंत्रण नहीं है। नीचे बताया गया है कि लॉक किस पर टिका है,
        और उसके विफल होने के लिए आपको क्या मानना होगा।
      </p>

      <h3>drand: एक सार्वजनिक घड़ी जो समय पर हस्ताक्षर करती है</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext> एक नेटवर्क है जिसे{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext> चलाती है, स्वतंत्र संगठनों का एक समूह (इनमें Cloudflare, Protocol Labs, Kudelski
          Security, EPFL और University College London शामिल हैं), जो मिलकर हर 3 सेकंड में एक यादृच्छिक, हस्ताक्षरित मान प्रकाशित करते हैं। मानों पर क्रमांक होते हैं: राउंड 1,
          राउंड 2, और इसी तरह आगे, हर एक किसी ज्ञात पल के लिए। कोई भी उन्हें कई सार्वजनिक रिले से पढ़ सकता है; भविष्य का मान कोई नहीं पढ़ सकता, क्योंकि वह अभी
          मौजूद ही नहीं है।
        </li>
        <li>
          हर राउंड पर थ्रेशोल्ड क्रिप्टोग्राफ़ी से हस्ताक्षर होते हैं (<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">BLS हस्ताक्षर</Ext>): हर संगठन के पास एक गुप्त कुंजी का एक हिस्सा होता है, और हस्ताक्षर के लिए
          अधिकांश हिस्से चाहिए। कोई अकेला संगठन, और संगठनों का कोई अल्पसंख्यक समूह, किसी राउंड का हस्ताक्षर उसके समय से पहले नहीं बना सकता। यह नेटवर्क
          2020 से चल रहा है; जो चेन हम इस्तेमाल करते हैं,{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>, वह 2023 से।
          उसकी सार्वजनिक कुंजी हमारे कोड में तय है, और हमें मिलने वाला हर संकेत उसी से जाँचा जाता है।
        </li>
        <li>
          <b>समय-लॉक एन्क्रिप्शन</b> (<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>, जिसे Protocol Labs और Cloudflare के शोधकर्ताओं ने
          2023 में प्रकाशित किया) उस घड़ी को लॉक में बदल देता है: संदेश
          किसी राउंड संख्या के लिए एन्क्रिप्ट होता है, और उस राउंड का हस्ताक्षर, प्रकाशित होने के बाद, उसे खोलने वाली अकेली कुंजी है। जब तक drand उसे प्रकाशित नहीं करता, वह कुंजी
          दुनिया में कहीं मौजूद नहीं होती।
        </li>
      </ul>

      <h3>Vrelis इसका इस्तेमाल कैसे करता है</h3>
      <ul>
        <li>
          आपका ब्राउज़र पाठ और आपकी फ़ाइलों की कुंजियों को आपके चुने समय के drand राउंड के लिए एन्क्रिप्ट करता है; नई कैप्सूल पृष्ठ भेजने से पहले राउंड संख्या दिखाता है।
          निजी कैप्सूल दूसरी बार प्राप्तकर्ता की कुंजी के लिए एन्क्रिप्ट होती है, ताकि समय के बाद केवल वही उसे खोल सके।
        </li>
        <li>
          हमारा सर्वर केवल सीलबंद परिणाम प्राप्त करता है और उसे सहेजता है। उसके पास कोई ऐसी कुंजी नहीं है जो कैप्सूल खोले: न समय से पहले, और निजी कैप्सूल के लिए
          समय के बाद भी नहीं। डेटाबेस लीक होने पर सिफ़रटेक्स्ट और मेटाडेटा उजागर होंगे, कोई संदेश कभी नहीं।
        </li>
        <li>
          समय आने पर आपका ब्राउज़र (या ऑफ़लाइन खोलने वाला साधन) चार सार्वजनिक drand रिले में से किसी एक से राउंड का हस्ताक्षर लाता है, उसे तय सार्वजनिक कुंजी से
          जाँचता है, और डिक्रिप्ट करता है। इस चरण में Vrelis की कोई भूमिका नहीं है।
        </li>
        <li>
          हर कैप्सूल पर उसके लेखक की कुंजी से हस्ताक्षर होते हैं (<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>, उसी तरह की कुंजी जो Bitcoin
          और <Ext href="https://nostr.com">Nostr</Ext> इस्तेमाल करते हैं), इसलिए बदली गई कैप्सूल पकड़ी जाती है। कुंजी आपके ब्राउज़र में आपके 12 शब्दों से बनती है (
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>) और बिना एन्क्रिप्शन के कभी उससे बाहर नहीं जाती।
        </li>
      </ul>

      <h3>अगर Vrelis बंद हो जाए</h3>
      <ul>
        <li>
          फ़ॉर्मेट खुले हैं: कैप्सूल एक <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">Nostr इवेंट</Ext> है (निजी कैप्सूल के लिए{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">NIP-59 gift wrap</Ext>) जिसकी सामग्री एक{" "}
          <Ext href="https://age-encryption.org">age</Ext> फ़ाइल है, जिसमें एक <Ext href="https://github.com/drand/tlock-js">tlock</Ext> स्टैंज़ा होता है। इन खुले विनिर्देशों को
          लागू करने वाला कोई भी सॉफ़्टवेयर आपकी कैप्सूल खोल सकता है; हमारा सॉफ़्टवेयर ज़रूरी नहीं है।
        </li>
        <li>
          <b>डाउनलोड</b> कैप्सूल को आपकी अपनी डिस्क पर रखता है, ऑफ़लाइन खोलने वाले साधन के साथ: एक HTML फ़ाइल जो उन्हें आपके कंप्यूटर पर खोलती है, बिना किसी
          Vrelis सर्वर के, ज़रूरी drand संकेत और, निजी कैप्सूल के लिए, आपकी कुंजी इस्तेमाल करके। पूरा एक्सपोर्ट vrelis.com के बिना खोला जा सकता है, अगर
          ज़रूरी संकेत उपलब्ध हो और आपके पास उसकी ज़रूरत की फ़ाइलें और कुंजियाँ बनी रहें।
        </li>
        <li>
          अगर drand खुद कभी ज़रूरी भविष्य का संकेत बनाने से पहले बंद हो जाए, तो वे कैप्सूल हमेशा के लिए लॉक रह सकती हैं। उन्हें पहले से
          डाउनलोड करने से यह जोखिम दूर नहीं होता। जिस कैप्सूल के खुलने की तारीख बीत चुकी है, उसे भी अपना सटीक, जाँचा हुआ संकेत चाहिए: अगर वह संकेत आपके
          आर्काइव में नहीं है या किसी रिले से उपलब्ध नहीं है, तो केवल तारीख के आधार पर आप उसे ऑफ़लाइन नहीं खोल सकते। पूरे एक्सपोर्ट और उनकी ज़रूरत की कुंजियाँ सँभालकर रखें।
        </li>
      </ul>

      <h3>इसके विफल होने के लिए आपको क्या मानना होगा</h3>
      <ul>
        <li>
          कि drand के अधिकांश संगठन मिलकर किसी राउंड पर समय से पहले हस्ताक्षर कर दें। वे स्वतंत्र हैं, कई देशों में हैं, और चेन सार्वजनिक है, इसलिए
          ऐसा हस्ताक्षर सबको दिख जाएगा। पूरी प्रणाली इसी भरोसे की धारणा पर टिकी है।
        </li>
        <li>
          कि वेब पृष्ठ झूठ बोले। पृष्ठ वह कोड है जो Vrelis आपके ब्राउज़र को देता है; जिसका उस कोड पर नियंत्रण है, वह पाठ को सील होने से पहले, या आपकी
          कुंजी को अनलॉक होने के बाद पढ़ सकता है। इसीलिए कैप्सूल डाउनलोड करके ऑफ़लाइन खोली जा सकती हैं, इसीलिए कुंजियों के फ़िंगरप्रिंट दिखाए जाते हैं ताकि आप उन्हें
          Vrelis के बाहर मिला सकें, और इसीलिए पायलट के बाद एक फ़ोन ऐप की योजना है जो कुंजी को वेब पृष्ठ से दूर रखे।
        </li>
        <li>
          कि आज की क्रिप्टोग्राफ़ी टूट जाए। एलिप्टिक-कर्व क्रिप्टोग्राफ़ी, जिसे drand और आपकी कुंजी दोनों इस्तेमाल करते हैं, भविष्य के{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">क्वांटम कंप्यूटरों</Ext> से टूट सकती है।
          दशकों तक सीलबंद रहने वाली कैप्सूल के लिए यह एक वास्तविक जोखिम है, जिसे पायलट हल नहीं करता।
        </li>
      </ul>

      <h2>शुरुआत करना</h2>
      <ol>
        <li>
          अगर प्रशासक ने आपका पता स्वीकृत किया है, तो सीधे उसी से पंजीकरण करें; नहीं तो अपना निमंत्रण लिंक खोलें और उसका पता इस्तेमाल करें। उस Google खाते से लॉग इन करें (Google, Vrelis को केवल आपका
          नाम और ईमेल बताता है), या पासवर्ड से (पते की पुष्टि के लिए हम एक लिंक मेल करते हैं)। बाद में आप लॉग इन के लिए पासकी जोड़ सकते हैं।
        </li>
        <li>
          Vrelis आपके ब्राउज़र में आपकी <b>कुंजी</b> बनाने की पेशकश करता है: आपका मुख्य हस्ताक्षर, जो आपके भेजे पर हस्ताक्षर करता है और आपको भेजी गई निजी कैप्सूल खोलता है। आप
          इसे छोड़कर बाद में खाता, कुंजियाँ में बना सकते हैं; कुंजी के बिना आप केवल सार्वजनिक कैप्सूल पढ़ते हैं। आपका <b>पुनर्प्राप्ति वाक्यांश</b> (12 शब्द जो
          किसी भी उपकरण पर कुंजी फिर से बनाते हैं) दिखने से पहले, आपसे पक्का करने को कहा जाता है कि कोई आपकी स्क्रीन देख या रिकॉर्ड नहीं कर रहा।
        </li>
        <li>12 शब्द कागज़ पर लिखें। आपसे उनमें से तीन वापस लिखने को कहा जाता है, फिर यह पुष्टि करने के लिए फिर से लॉग इन करने को कि यह आप हैं: अपने पासवर्ड या पासकी से, या अपनी सबसे पहली कुंजी के लिए Google से।</li>
        <li>
          एक PIN चुनें (कम से कम {l.pinDigits} अंक) और एक <Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">पासकी</Ext> बनाएँ (फ़िंगरप्रिंट,
          चेहरा या आपके उपकरण का PIN)। ये दोनों मिलकर इस उपकरण पर कुंजी की सुरक्षा करते हैं,
          और उस प्रति की भी जो Vrelis नए उपकरण पर ले जाने के लिए रखता है। अगर आपका ब्राउज़र इस तरह पासकी इस्तेमाल नहीं कर सकता, तो कुंजी उस ब्राउज़र में बिना सुरक्षा के रहती है और
          सर्वर पर कोई प्रति नहीं होती; तब दूसरे उपकरण तक पहुँचने का अकेला रास्ता वाक्यांश है।
        </li>
      </ol>

      <h2>आपका पुनर्प्राप्ति वाक्यांश</h2>
      <ul>
        <li>इसे कागज़ पर, किसी सुरक्षित जगह रखें। इसकी फ़ोटो न लें और इसे कंप्यूटर या पासवर्ड मैनेजर में न रखें।</li>
        <li>
          जिसके पास 12 शब्द हैं, वह <b>आप ही</b> है: वह आपके रूप में हस्ताक्षर कर सकता है और आपकी निजी कैप्सूल खोल सकता है। अगर वे लीक हो जाएँ, तो खाता,
          कुंजियाँ में <b>कुंजी रीसेट करें</b> इस्तेमाल करें: आप वाक्यांश लिखते हैं, फिर से लॉग इन करते हैं, और कुंजी हमेशा के लिए रद्द हो जाती है; अब कोई उससे या उसके लिए कुछ नहीं भेज सकता। आप बताते हैं कि वह कब से
          चोरी हुई हो सकती है, और तब से उससे भेजी गई कैप्सूल “रद्द की गई कुंजी” के रूप में चिह्नित होती हैं। फिर आप नए वाक्यांश के साथ नई कुंजी बनाते हैं।
        </li>
        <li>
          आपकी पुरानी कुंजियाँ एक <b>कुंजी-संग्रह</b> में जा सकती हैं, जो आपकी वर्तमान कुंजी से एन्क्रिप्टेड होता है, ताकि पुरानी कुंजी को भेजी गई कैप्सूल पुराने वाक्यांश के बिना भी खुलें।
          आप पुराना वाक्यांश खुद भी रख सकते हैं और उसे बाद में कुंजी-संग्रह में जोड़ सकते हैं।
        </li>
        <li>
          अगर आपका उपकरण खो जाए: किसी दूसरे पर लॉग इन करें और अपनी पासकी और PIN (अगर आपकी पासकी सिंक होती है) या अपना वाक्यांश इस्तेमाल करें। अगर आप PIN भूल जाएँ:
          वाक्यांश इस्तेमाल करें। {l.pinAttempts} गलत PIN के बाद सर्वर पर रखी प्रति ब्लॉक हो जाती है; वाक्यांश नई प्रति लिखता है।
        </li>
        <li>
          अगर आप वाक्यांश <b>और</b> अपनी कुंजी वाला हर उपकरण खो देते हैं, तो आपकी निजी कैप्सूल कभी नहीं खोली जा सकतीं, किसी के भी द्वारा। प्रशासक आपको केवल एक
          नई पहचान दे सकता है। कुंजी रद्द करने के लिए भी वाक्यांश चाहिए, इसलिए उसके बिना प्रशासक आपके लिए उसे रद्द करता है।
        </li>
      </ul>

      <h2>कैप्सूल लिखना</h2>
      <ul>
        <li>
          <b>पायलट के सभी लोग</b> (सार्वजनिक) या <b>एक सदस्य</b> (निजी)। सार्वजनिक कैप्सूल उसके समय के बाद हर सदस्य पढ़ सकता है; निजी कैप्सूल केवल
          वह व्यक्ति जिसे आपने लिखा, और आप, अगर आप प्रति रखते हैं (डिफ़ॉल्ट रूप से चालू)।
        </li>
        <li>
          हर कैप्सूल का एक <b>विषय</b> होना ज़रूरी है। वह कभी एन्क्रिप्ट नहीं होता: कैप्सूल देखने वाला कोई भी उसे तुरंत पढ़ता है, और हमारा सर्वर भी।
        </li>
        <li>
          <b>परिचय</b> वैकल्पिक है: अधिकतम {l.introChars} अक्षर, जो समय से पहले, कैप्सूल पर क्लिक करने पर पढ़े जा सकते हैं। सार्वजनिक कैप्सूल पर
          इसे सभी पढ़ते हैं, हमारा सर्वर भी। निजी कैप्सूल पर केवल प्राप्तकर्ता इसे अपनी कुंजी से पढ़ता है; सर्वर इसे नहीं देखता। संदेश और उसकी
          फ़ाइलें दोनों ही स्थितियों में लॉक रहती हैं।
        </li>
        <li>
          निजी कैप्सूल किसने भेजी, यह प्राप्तकर्ता की कुंजी के लिए सीलबंद होता है: वह इसे अपनी कुंजी से कैप्सूल खोलते ही देख लेता है, उसके समय से बहुत पहले।
          जिसके पास वह कुंजी नहीं है, उसे कैप्सूल में केवल विषय और समय दिखता है। हमारा सर्वर जानता है कि उसे किस खाते ने भेजा। वह यह केवल प्राप्तकर्ता को बताता है, उसकी कुंजी के लिए एन्क्रिप्ट करके, ताकि ऐप जाँच सके कि कैप्सूल पर हस्ताक्षर करने वाली कुंजी उसी खाते की है; नहीं तो वह नाम की जगह “असत्यापित प्रेषक” दिखाता है।
        </li>
        <li>
          <b>प्रेषक का नाम छिपाएँ</b> (निजी कैप्सूल): प्राप्तकर्ता की सूचियों में आपके नाम की जगह “छिपा हुआ प्रेषक” दिखता है, जब तक वह कैप्सूल पर क्लिक न करे, ताकि उसके
          कंधे के ऊपर से झाँकने वाला कोई न देखे कि उसे कौन लिखता है। प्राप्तकर्ता फिर भी समय से पहले देख सकता है कि यह आप हैं।
        </li>
        <li>
          चुनें कि यह कब खुले: 5 मिनट, एक घंटे, एक दिन, एक सप्ताह, एक वर्ष में, या वर्ष 2125 तक की कोई तारीख। भेजने से पहले असली खुलने का समय दिखाया जाता है; वह
          कुछ सेकंड तक सटीक होता है।
        </li>
        <li>
          पाठ: सार्वजनिक कैप्सूल के लिए अधिकतम {l.publicText}, निजी के लिए {l.privateText}। फ़ाइलें: अधिकतम {l.files} फ़ाइलें और, कुल मिलाकर, मुफ़्त प्लान पर {l.fileBytes} या लेगेसी या स्टार्ट के साथ {l.planFileBytes}। फ़ोटो
          छोटी की जाती हैं और स्थान सहित उनका मेटाडेटा हटा दिया जाता है।
        </li>
        <li>
          <b>सील करके भेजें</b> कैप्सूल पर हस्ताक्षर करने के लिए एक बार आपका PIN और पासकी माँगता है। उसके बाद कोई उसे बदल नहीं सकता, और कोई उसे उसके
          समय से पहले नहीं पढ़ सकता।
        </li>
        <li>
          किसी को निजी रूप से लिखने से पहले उसकी कुंजी जाँचें: नई कैप्सूल पृष्ठ उसका फ़िंगरप्रिंट (16 अक्षर) दिखाता है। अगर इस ब्राउज़र के पिछली बार देखने के बाद उसकी कुंजी
          बदल गई है, तो नई कैप्सूल पृष्ठ आपको चेतावनी देता है और तब तक रुकता है जब तक आप Vrelis के बाहर उससे पुष्टि न कर लें कि नई कुंजी सचमुच उसी की है।
        </li>
      </ul>

      <h2>पढ़ना</h2>
      <ul>
        <li>
          <b>सार्वजनिक संदेश</b>: सार्वजनिक कैप्सूल। <b>इनबॉक्स</b>: आपको भेजी गई निजी कैप्सूल। <b>भेजे गए</b>: आपकी। <b>निगरानी में</b>: सार्वजनिक कैप्सूल जिनकी आप प्रतीक्षा करते हैं। केवल किसी
          लेखक की कैप्सूल देखने के लिए उसे फ़ॉलो करें।
        </li>
        <li>
          किसी सदस्य को <b>छिपाएँ</b>: उसकी सार्वजनिक कैप्सूल हर उपकरण पर आपके सार्वजनिक संदेश से हट जाती हैं; उसके लिए कुछ नहीं बदलता। किसी सदस्य को <b>ब्लॉक करें</b>: वह
          अब आपके इनबॉक्स में निजी कैप्सूल नहीं डाल सकता (नई कैप्सूल पृष्ठ उसे भेजने से पहले बता देता है); आप फिर भी उसे फ़ॉलो कर सकते हैं और उसकी सार्वजनिक कैप्सूल पढ़ सकते हैं।
          उसकी पहले की कैप्सूल इनबॉक्स, ब्लॉक किए गए में चली जाती हैं, और फिर भी खुलती हैं। दोनों विकल्प किसी व्यक्ति के मेनू में और लोग पृष्ठ पर हैं, साथ में यह भी कि आपको कौन फ़ॉलो करता है।
          Vrelis केवल उसी की ज़िम्मेदारी लेता है जो उसे मिलता है और जो वह पहुँचाता है: Vrelis के बाहर आपकी सार्वजनिक कुंजी के लिए एन्क्रिप्ट करने से किसी को कोई नहीं रोक सकता।
        </li>
        <li>
          निजी कैप्सूल तब तक कुछ नहीं दिखाती जब तक आप उसे खोलें नहीं: <b>खोलें</b> आपका PIN माँगता है, फिर दिखाता है कि उसे किसने भेजा और वह कब खुलेगी, और पाठ उस
          समय के आने पर।
        </li>
        <li>
          आपकी कुंजी लॉक रहती है; उसकी ज़रूरत वाली हर कार्रवाई PIN माँगती है। एक के बाद एक कई कैप्सूल पढ़ने के लिए, ऊपर की पट्टी में <b>… मिनट के लिए अनलॉक करें</b> इस्तेमाल
          करें। <b>लॉक करें</b> सब कुछ निजी एक साथ छिपा देता है; पृष्ठ छोड़ने पर भी ऐसा होता है।
        </li>
        <li>
          <a href="/account">खाता</a>, गोपनीयता में: “मेरी सूचियों में प्रेषक केवल कैप्सूल पर क्लिक करने के बाद दिखाएँ” हर निजी कैप्सूल को छिपे प्रेषक वाली मानता है,
          हर उपकरण पर।
        </li>
        <li>
          सूचियाँ एक बार में 50 कैप्सूल दिखाती हैं; <b>और दिखाएँ</b> अगली जोड़ता है। Vrelis उन्हें फ़िल्टर और क्रमबद्ध करता है (खुलने के समय से या इस आधार पर कि उसे वे कब
          मिलीं), इसलिए फ़िल्टर या समूह Vrelis को आपके चुने सदस्यों की id भेजता है, कभी समूह या उसका नाम नहीं। अपने इनबॉक्स को प्रेषक से फ़िल्टर करने के लिए
          आपकी कुंजी चाहिए, इसमें छिपे प्रेषक वाली कैप्सूल छूट जाती हैं, और जब प्रेषक केवल क्लिक के बाद दिखते हैं तब यह बंद रहता है।
        </li>
        <li>
          <b>खोज</b> विषय, सार्वजनिक कैप्सूल का परिचय, सार्वजनिक कैप्सूल किसने लिखी और आपने किसे भेजी, यह देखती है। यह कभी खोला गया पाठ नहीं देखती
          (वह केवल आपके उपकरण पर है), न ही यह कि आपको निजी कैप्सूल किसने भेजी।
        </li>
      </ul>

      <h2>उत्तर लिफ़ाफ़ा, CC और पत्राचार</h2>
      <ul>
        <li>
          निजी संदेश के साथ एक <b>उत्तर लिफ़ाफ़ा</b> जा सकता है: आप हर प्राप्तकर्ता से अपने लिए एक निजी उत्तर माँगते हैं, जो आपकी कुंजी के लिए सीलबंद होता है।
          वे उत्तर दे सकते हैं, भले ही वे आपको फ़ॉलो न करते हों। आप चुनते हैं कि हर उत्तर आपके तय समय पर खुले (उस समय के बाद कोई उत्तर
          स्वीकार नहीं होता) या प्राप्तकर्ता के चुने समय पर। आप कैप्सूल और उत्तर का भुगतान पहले ही कर देते हैं: प्रति प्राप्तकर्ता 1 की जगह 2 स्टैम्प। प्राप्तकर्ता के पहले उत्तर पर उसका कुछ खर्च नहीं होता; दूसरा उत्तर उसका एक स्टैम्प लेता है, और आप दोनों देखते हैं।
        </li>
        <li>
          <b>CC</b>: एक सदस्य को भेजा गया निजी संदेश एक साथ और सदस्यों को भी जा सकता है, और सूची में शामिल हर व्यक्ति देखता है कि और किसे मिला। सूची
          प्रेषक के हस्ताक्षर के साथ संदेश के अंदर सीलबंद होती है; हमारा सर्वर उसे कभी नहीं देखता। CC के बिना समूह को भेजना गोपनीय रहता है: कोई नहीं देखता कि
          और किसे मिला।
        </li>
        <li>
          उत्तर लिफ़ाफ़े वाले संदेश और उनके उत्तर <b>पत्राचार</b> पृष्ठ पर रहते हैं, इनबॉक्स या भेजे गए में नहीं: हर
          अनुरोध के नीचे आप हर प्राप्तकर्ता के उत्तर क्रम से देखते हैं, उनके समय तक लॉक, और यह भी कि हर एक का भुगतान किसने किया। उत्तर केवल प्रेषक को जाता है।
        </li>
      </ul>

      <h2>Vrelis के बिना कैप्सूल रखना</h2>
      <ul>
        <li>
          किसी कैप्सूल पर <b>डाउनलोड</b> उसे एक फ़ाइल के रूप में सहेजता है; अटैचमेंट होने पर यह फ़ाइलों और ऑफ़लाइन खोलने वाले साधन के साथ एक .zip होती है। कई चुनें और एक आर्काइव के लिए{" "}
          <b>चुनी गई डाउनलोड करें</b> इस्तेमाल करें (“दिखाई गई सभी चुनें” दिखाई गई कैप्सूल चुनता है)।
        </li>
        <li>
          ऑफ़लाइन खोलने वाला साधन एक HTML फ़ाइल है (<a href="/account">खाता</a>, या .zip के अंदर)। यह आपकी कैप्सूल आपके अपने कंप्यूटर पर खोलता है, निजी कैप्सूल आपके वाक्यांश से। यह
          कभी vrelis.com से संपर्क नहीं करता; अगर फ़ाइल में खुलने का संकेत अभी नहीं है, तो यह केवल सार्वजनिक drand समय सेवा से वह संकेत माँगता है।
        </li>
        <li>
          “खोला गया पाठ और फ़ाइलें शामिल करें” खोले गए पाठ और फ़ाइलों को डाउनलोड में बिना एन्क्रिप्शन के रखता है: फ़ाइल वाला कोई भी उन्हें पढ़ सकता है।
        </li>
      </ul>

      <h2>यह उपकरण</h2>
      <ul>
        <li>
          <b>लॉक करें</b>: कुंजी को मेमोरी से हटाता है। <b>लॉग आउट</b>: यह सत्र समाप्त करता है; कुंजी इस उपकरण पर रहती है। <b>सभी उपकरणों से लॉग आउट</b>:
          हर उपकरण पर आपके सत्र समाप्त करता है। <b>इस उपकरण का डेटा मिटाएँ</b>: आपकी कुंजी और वह सब कुछ मिटाता है जो Vrelis ने इस ब्राउज़र में सहेजा है।
        </li>
        <li>एक लॉग इन {l.sessionDays} दिन चलता है।</li>
      </ul>

      <h2>Vrelis क्या देख सकता है और क्या नहीं</h2>
      <ul>
        <li>
          <b>Vrelis नहीं पढ़ सकता</b>: कोई कैप्सूल उसके समय से पहले, कोई निजी कैप्सूल कभी भी (उसके पास उसकी कुंजी नहीं है), या आपकी कुंजी, PIN या वाक्यांश।
        </li>
        <li>
          <b>Vrelis देखता है</b>: आपका नाम और ईमेल (मालिक और प्रशासक ईमेल देखते हैं), कौन कैप्सूल भेजता है और किसे, वह कब भेजी गई, सार्वजनिक कैप्सूल कब
          खुलती है, आकार, और यह कि कैप्सूल में फ़ाइलें हैं। समय के बाद सार्वजनिक कैप्सूल हर सदस्य पढ़ सकता है, और इसलिए Vrelis भी।
        </li>
        <li>
          कुंजी-संग्रह और आपकी कुंजी की प्रतियाँ केवल एन्क्रिप्टेड रूप में सहेजी जाती हैं; Vrelis उन्हें नहीं खोल सकता।
        </li>
        <li>
          हस्ताक्षर साबित करता है कि किस कुंजी ने कैप्सूल लिखी। “रद्द की गई कुंजी” से चिह्नित कैप्सूल पर ऐसी कुंजी से हस्ताक्षर हुए थे जिसे उसके मालिक ने बाद में चोरी हुई बताया। कुंजी किसी व्यक्ति की है, यह सदस्य सूची पर और उस नाम पर निर्भर करता है जो प्रेषक ने
          कैप्सूल में डाला। जिन लोगों को आप निजी रूप से लिखते हैं, उनके साथ फ़िंगरप्रिंट जाँचें।
        </li>
      </ul>

      <h2>प्लान और स्टैम्प</h2>
      <ul>
        <li>
          एक <b>स्टैम्प</b> एक प्राप्तकर्ता को एक डिलीवरी का भुगतान करता है। आपकी अपनी प्रति मुफ़्त है। मुफ़्त प्लान हर महीने {l.freeStamps} स्टैम्प, {l.freeStorage} स्टोरेज और प्रति संदेश {l.fileBytes} की फ़ाइलें देता है; लेगेसी
          (हर महीने {l.legacyStamps} स्टैम्प, {l.planStorage}, {l.planFileBytes}) पहले सदस्यों को दिया गया; स्टार्ट हर महीने {l.planStamps} स्टैम्प देगा। एक महीने के स्टैम्प आगे नहीं जुड़ते, और वे अगले महीने के
          पहले दिन (UTC) फिर से मिलते हैं। <b>बिलिंग</b> दिखाता है कि आपके पास क्या है, क्या बचा है और क्या योजना में है।
        </li>
        <li>
          फ़िलहाल कोई शुल्क नहीं लिया जाता और कुछ भी खरीदा नहीं जा सकता: प्लान केवल पूर्वावलोकन हैं, और प्रशासक किसी अनुरोध को हाथ से चालू करता है। आपने जो पहले भेजा है, वह आपके और जिन्हें आपने भेजा उनके लिए पढ़ने योग्य रहता है, आपके प्लान या स्टैम्प के साथ
          कुछ भी हो।
        </li>
        <li>
          अपने लिए प्रति वैकल्पिक है और डिफ़ॉल्ट रूप से चालू है। अगर आप इसे बंद करते हैं, तो वह संदेश केवल प्राप्तकर्ता ही कभी पढ़ सकता है: आप उसे खुद नहीं पढ़ पाएँगे, खुलने के बाद भी नहीं।
        </li>
      </ul>

      <h2>पायलट की सीमाएँ</h2>
      <ul>
        <li>
          प्रति दिन अधिकतम {l.daily} डिलीवरी (आपकी अपनी प्रति मुफ़्त है; समूह को भेजी गई कैप्सूल हर सदस्य के लिए एक गिनी जाती है)। अपलोड: 24 घंटों में {l.uploadDaily} और {l.unusedFiles} अप्रयुक्त फ़ाइलें;
          जो फ़ाइलें 24 घंटों के भीतर किसी कैप्सूल में इस्तेमाल नहीं होतीं, वे मिटा दी जाती हैं।
        </li>
        <li>Google, पासवर्ड या पासकी से लॉग इन करें। कोई भुगतान नहीं, कोई सार्वजनिक नेटवर्क नहीं: कैप्सूल पायलट के अंदर ही रहती हैं।</li>
        <li>
          ऐसे पते को आमंत्रित करने पर जिसका पहले से खाता है, उस सदस्य को इसके बजाय जुड़ने का अनुरोध जाता है; अगर वह स्वीकार करता है, तो आप एक-दूसरे को फ़ॉलो करते हैं। दोनों स्थितियों में आपकी
          स्क्रीन एक जैसी दिखती है, इसलिए कोई निमंत्रणों से यह पता नहीं लगा सकता कि कौन सदस्य है।
        </li>
      </ul>

      <h2>डेवलपर के लिए</h2>
      <p>
        सेवाएँ <a href="/developers">Vrelis API</a> के माध्यम से कैप्सूल पढ़ और भेज सकती हैं। किसी सेवा को प्रशासक के सेवा निमंत्रण से अपना खाता
        मिलता है; बाकी सबके लिए वह एक सामान्य सदस्य है। वह अपनी कुंजी खुद बनाती है और हर कैप्सूल पर खुद हस्ताक्षर करती है; उसके API टोकन पढ़ और भेज सकते हैं, लेकिन
        कुंजियाँ या लॉग इन के तरीके प्रबंधित नहीं कर सकते। आप किसी सेवा को किसी और की तरह ही ब्लॉक कर सकते हैं या छिपा सकते हैं।
      </p>

      <h2>पायलट सदस्यों से हमारा अनुरोध</h2>
      <ol>
        <li>एक सार्वजनिक कैप्सूल भेजें जो एक घंटे में खुले, और किसी दूसरे सदस्य को एक निजी कैप्सूल जो एक दिन में खुले।</li>
        <li>सार्वजनिक कैप्सूल खुलने के बाद पढ़ें, और किसी एक को पहले पढ़ने की कोशिश करें: ऐसा संभव नहीं होना चाहिए।</li>
        <li>आप में से कोई एक: दूसरे उपकरण पर अपनी कुंजी सेट करें और वहाँ अपना इनबॉक्स पढ़ें।</li>
        <li>आप में से कोई एक: किसी दूसरे सदस्य को छिपाएँ और जाँचें कि उसकी कैप्सूल केवल आपके सार्वजनिक संदेश से गायब होती हैं।</li>
      </ol>

      <h2>समस्याएँ</h2>
      <p>
        हर पृष्ठ के नीचे <b>समस्या की सूचना दें</b> इस्तेमाल करें, या {link} पर लिखें। बताएँ कि आपने क्या किया, आप क्या उम्मीद कर रहे थे और क्या हुआ; स्क्रीनशॉट मदद करता है।
        सेवा, उसकी सीमाएँ और ज़िम्मेदारियाँ <a href="/terms">उपयोग की शर्तें</a> में बताई गई हैं।
      </p>
    </>
  );
}
